// src/components/GenericDropdown.tsx
import React, {useState, useRef, useEffect} from 'react'

/**
 * Generic item interface for dropdown entries.
 * Must include id, first_name, last_name, and email.
 */
export interface GenericItem {
  id: number
  uuid?: string
  first_name?: string
  last_name?: string
  email?: string
  status?: number
  name?: string 
}

export interface GenericDropdownProps {
  /** Array of items to display */
  items: GenericItem[]
  /** Currently selected item's id, or null */
  selectedId: number | null
  /** Callback invoked with the selected item's id */
  onSelect: (id: number) => void
  /** Placeholder text when nothing is selected */
  placeholder?: string
  /** Maximum height of the dropdown list */
  maxHeight?: string
}

const GenericDropdown: React.FC<GenericDropdownProps> = ({
  items,
  selectedId,
  onSelect,
  placeholder = 'Select an option',
  maxHeight = '300px',
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const toggle = () => setOpen((prev) => !prev)
  const handleItemClick = (id: number) => {
    onSelect(id)
    setOpen(false)
  }

  // Close when clicking outside
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  // Determine label for selected item
  const selected = items.find((item) => item.id === selectedId)
  const label = selected
    ? selected.name ?? `${selected.first_name || ''} ${selected.last_name || ''}`.trim()
    : placeholder

  return (
    <div className='dropdown w-100' ref={ref}>
      <button type='button' className='w-100 fw-bold border p-2 text-start' onClick={toggle}>
        {label}
        {!selectedId && <i className='fas fa-caret-down ms-2 text-black'></i>}
      </button>
      {open && (
        <ul className='dropdown-menu w-100 show' style={{maxHeight, overflowY: 'auto'}}>
          {items.map((item) => (
            <li key={item.id}>
              <button
                type='button'
                className={`dropdown-item ${selectedId === item.id ? 'active' : ''}`}
                onClick={() => handleItemClick(item.id)}
              >
                <strong>
                  {item.name ?? `${item.first_name || ''} ${item.last_name || ''}`}
                </strong>
                <br />
                <small>{item.email}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default GenericDropdown
